import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Motion";
import { Seo } from "@/components/Seo";
import { whatsappWithText } from "@/lib/site";
import { WhatsAppIcon } from "@/components/WhatsAppButton";

const TIERS = [
  {
    name: "Basic",
    prices: { INR: "₹4,999", EUR: "€55", USD: "$59" },
    unit: "starting from",
    desc: "For assignments, essays, case studies, and coursework at any level.",
    features: [
      "Written from scratch by a subject expert",
      "Structured to your rubric and guidelines",
      "Referencing in APA, MLA, Harvard, or your style",
      "One free revision round",
      "Plagiarism check on every delivery",
      "Delivery typically in 3–7 days",
    ],
    popular: false,
  },
  {
    name: "Standard",
    prices: { INR: "₹9,999", EUR: "€109", USD: "$119" },
    unit: "starting from",
    desc: "For MBA projects, full reports, research papers, and master's-level work.",
    features: [
      "Dedicated writer from your discipline",
      "Chapter-wise delivery with review windows",
      "Data analysis support (SPSS, R, Excel)",
      "Two free revision rounds",
      "Formatting to your university's template",
      "Plagiarism report included with delivery",
      "Viva and presentation notes on request",
    ],
    popular: true,
  },
  {
    name: "Premium",
    prices: { INR: "₹39,999", EUR: "€429", USD: "$469" },
    unit: "starting from",
    desc: "For PhD and doctorate-level theses, dissertations, and publication work.",
    features: [
      "Senior doctoral-level writer assigned to you",
      "Full thesis support — synopsis to final submission",
      "Advanced statistical and qualitative analysis",
      "Journal-ready chapters and manuscripts",
      "Unlimited scoped revisions",
      "Viva preparation and defence support",
      "Priority timelines and direct communication",
    ],
    popular: false,
  },
];

const FACTORS = [
  { title: "Length & depth", text: "A short essay and a 200-page thesis are very different jobs. The bigger and deeper the work, the more time it needs from our team." },
  { title: "Your deadline", text: "More time means more flexibility on price. Urgent work is often possible, but express delivery adds to the quote." },
  { title: "Research type", text: "Work that needs surveys, interviews, or field data takes more effort than desk research — and the quote reflects that honestly." },
  { title: "Analysis required", text: "Advanced statistics, modelling, or legal analysis need our senior specialists, which shapes the final price." },
];

const detectCurrency = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.startsWith("Europe/")) return "EUR";
    if (tz.startsWith("America/")) return "USD";
  } catch {}
  return "INR";
};

export default function Pricing() {
  const [currency, setCurrency] = useState("INR");

  useEffect(() => {
    setCurrency(detectCurrency());
  }, []);

  return (
    <div data-testid="pricing-page">
      <Seo
        title="Pricing | MBA Project & Thesis Writing Help"
        description="Transparent academic writing packages from ₹4,999. Fixed quotes before work begins — final pricing based on scope, deadline, and complexity."
      />
      <PageHero
        eyebrow="Pricing"
        lines={["Honest pricing.", "Fixed before we start."]}
        sub="No word-count limits — pricing is based on your academic level and what the work actually needs. You'll always get a fixed quote before anything begins."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="currency-toggle">
          {["INR", "EUR", "USD"].map((c) => (
            <button
              key={c}
              onClick={() => setCurrency(c)}
              data-testid={`currency-${c.toLowerCase()}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold border transition-colors duration-200 ${
                currency === c
                  ? "bg-[#2563EB] text-white border-[#2563EB]"
                  : "bg-white text-slate-600 border-slate-300 hover:border-[#2563EB] hover:text-[#2563EB]"
              }`}
            >
              {c}
            </button>
          ))}
          <span className="ml-2 text-xs text-slate-400">Auto-detected from your region — switch anytime</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08} className="h-full">
              <div
                data-testid={`pricing-card-${t.name.toLowerCase()}`}
                className={`relative flex flex-col h-full rounded-2xl p-8 ${
                  t.popular
                    ? "bg-gradient-to-br from-[#1D4ED8] via-[#2563EB] to-[#4F46E5] text-white shadow-2xl shadow-blue-500/30 md:-translate-y-3"
                    : "bg-white border border-slate-200 shadow-sm"
                }`}
              >
                {t.popular && (
                  <span className="absolute -top-3 left-8 rounded-full bg-[#25D366] text-white text-xs font-semibold px-4 py-1">
                    Most Popular
                  </span>
                )}
                <h2 className="font-display font-bold text-xl tracking-tight">{t.name}</h2>
                <p className={`mt-2 text-sm leading-relaxed ${t.popular ? "text-white/60" : "text-slate-500"}`}>{t.desc}</p>
                <div className="mt-6">
                  <span className={`text-xs uppercase tracking-widest ${t.popular ? "text-white/50" : "text-slate-400"}`}>{t.unit}</span>
                  <p className="font-display font-extrabold text-4xl tracking-tight mt-1" data-testid={`price-${t.name.toLowerCase()}`}>{t.prices[currency]}</p>
                </div>
                <ul className="mt-8 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2.5 text-sm ${t.popular ? "text-white/85" : "text-slate-600"}`}>
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${t.popular ? "text-[#25D366]" : "text-[#2563EB]"}`} strokeWidth={2} />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={whatsappWithText(`Hi, I'm interested in the ${t.name} package. Can you share a custom quote?`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                  className={`mt-8 inline-flex justify-center items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-[opacity,transform] duration-200 hover:-translate-y-0.5 ${
                    t.popular ? "bg-[#25D366] text-white" : "bg-gradient-to-r from-[#2563EB] to-[#4F46E5] text-white hover:opacity-90"
                  }`}
                >
                  <WhatsAppIcon className="h-4 w-4" /> Get Custom Quote
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <p className="mt-6 text-xs text-slate-400 max-w-2xl" data-testid="currency-note">
          EUR and USD prices are approximate conversions shown for convenience. Your fixed quote is confirmed in your preferred currency before any payment.
        </p>

        <Reveal className="mt-20">
          <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">What decides your final quote?</h2>
          <p className="mt-3 text-slate-600 max-w-2xl">
            No word-count traps and no hidden charges. Four simple things shape your price — and we explain each one before you pay anything.
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pricing-factors">
            {FACTORS.map((f, i) => (
              <div key={f.title} className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
                <span className="font-display font-extrabold text-3xl text-blue-200">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-display font-bold text-base tracking-tight">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal className="mt-16 border border-slate-200 rounded-2xl bg-[#F5F8FF] p-10 text-center">
          <h2 className="font-display font-bold text-2xl tracking-tight">Every project is different.</h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">
            Length, deadline, research depth, and analysis requirements all shape the final price. Send your brief for a personalised quote — it's free and there's no obligation.
          </p>
          <a
            href={whatsappWithText("Hi, here's my project brief for a custom quote:")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pricing-custom-quote-cta"
            className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5"
          >
            <WhatsAppIcon className="h-5 w-5" /> Request a Personalised Quote
          </a>
        </Reveal>
      </section>
    </div>
  );
}
